import { DeleteFilled, LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import { filterByBindItem } from '@knowledge/common/components/ItemSelectV5/components/QuickSelect';
import { ItemV5 } from '@knowledge/common/components/ItemSelectV5/types';
import { FilterBarValueProvider } from '@knowledge/knowledges/singleClass/context';
import { SpuSelectInner } from '@knowledge/knowledges/singleClass/NewSpuSelect';
import { T, useTranslate } from '@tolgee/react';
import { useRequest } from 'ahooks';
import { Button, Drawer, DrawerProps, Empty, Form, Input, message, Modal, Radio, Tabs } from 'antd';
import FormField from 'components/FormField';
import { Formik, FormikConfig, FormikProps } from 'formik';
import { ReactNode, useRef, useState } from 'react';
import {
  SaveKnowledge,
  SourceKnowledge,
  SupplementalKnowledgeAiReplyKnowledge,
  SupplementKnowledgeAnswer,
} from 'services/api/supplementKnowledge';
import { PureAnswerEditor } from '../Detail/AnswerEditor';
import { ItemSelectV5Drawer } from '@knowledge/common/components/ItemSelectV5/Variant';
import useModal from 'hooks/useModal';
import { uniqBy } from 'lodash-es';
import personImage from 'views/BootGuide/assets/person.png';
import styled from '@emotion/styled';
import { queryItemList } from '@knowledge/common/components/ItemSelectV2/api';
import { item2bindItem } from '../SpuMapping/ExpandSkuDrawer';

interface PageKnowledgeDrawerProps {
  conversation: Conversation.Conversation;
  knowledge: SourceKnowledge;
  updatedKnowledges?: SaveKnowledge[];
  translatedMap: Partial<Record<string, string>>;
}

function PageKnowledgeDrawer({
  kvId,
  pointId,
  conversation,
  knowledge,
  updatedKnowledges,
  translatedMap,
  onKnowledgesChange,
  ...otherProps
}: PageKnowledgeDrawerProps &
  DrawerProps & {
    kvId: number;
    pointId: string;
    onKnowledgesChange: (knowledges: SaveKnowledge[]) => void;
  }) {
  const { knowledgeId, source, bindItem } = knowledge;

  const { t } = useTranslate();

  const { loading, data } = useRequest(
    () => {
      return queryItemList({
        keyword: bindItem.map((_) => _.spuId).join(','),
        keywordType: 2,
        pageNo: 1,
        pageSize: bindItem.length,
        platformStoreScope: [],
        tagId: [],
        storeType: 0,
      }).then((res) => filterByBindItem(res.itemList, bindItem));
    },
    {
      onError: () => message.error(t('phrase.获取数据失败，请稍后再试')),
    },
  );

  return (
    <PageKnowledgeForm
      key={`${otherProps.visible}`}
      conversation={conversation}
      knowledge={knowledge}
      updatedKnowledges={updatedKnowledges}
      translatedMap={translatedMap}
      items={data}
      onSubmit={(values) => {
        onKnowledgesChange(
          values.map(({ key, value, bindItem }, index) => {
            return {
              knowledgeId: index === 0 ? knowledgeId : undefined,
              kvId,
              pointId,
              key,
              value,
              source,
              bindItem,
              operate: index === 0 ? 'UPDATE' : 'CREATE',
            };
          }),
        );
        otherProps.onClose?.({} as any);
      }}
    >
      {(form, { handleSubmit }) => {
        return (
          <Drawer
            title={t('修改引用知识')}
            width={1240}
            footer={
              <div className="flex justify-end">
                <Button className="mr-4" onClick={otherProps.onClose as any}>
                  {t('退出')}
                </Button>
                <Button type="primary" onClick={() => handleSubmit()}>
                  {t('确认修改内容')}
                </Button>
              </div>
            }
            {...otherProps}
          >
            <Tips>
              <img src={personImage} alt="person" />
              <div>
                <T keyName="cite-knowledge-page-edit-tips" params={{ b: <b /> }} />
              </div>
            </Tips>
            {data ? form : loading ? <LoadingOutlined /> : null}
          </Drawer>
        );
      }}
    </PageKnowledgeForm>
  );
}

type FV = Pick<SupplementalKnowledgeAiReplyKnowledge, 'key' | 'value' | 'bindItem'>[];

function PageKnowledgeForm({
  conversation,
  knowledge,
  updatedKnowledges,
  translatedMap,
  items,
  onSubmit,
  children,
}: PageKnowledgeDrawerProps &
  Pick<FormikConfig<FV>, 'onSubmit'> & {
    items?: ItemV5[];
    children: (form: ReactNode, props: FormikProps<FV>) => ReactNode;
  }) {
  const { t } = useTranslate();

  const { region, channel, storeId } = conversation;

  const { key: originKey, value: originValue, bindItem } = knowledge;
  const getKey = (key: string) => translatedMap[key] || key;
  const getValue = (value: SupplementKnowledgeAnswer[]) =>
    value.map((answer) => {
      const { msgType, message } = answer;
      if (msgType === 'TEXT' && message && translatedMap[message]) {
        return {
          ...answer,
          message: translatedMap[message],
        };
      }
      return answer;
    });
  const key = getKey(originKey);
  const value = getValue(originValue);
  const firstInitialValue = {
    key,
    value,
    bindItem,
  };
  const defaultInitialValue = {
    key,
    value,
    bindItem: [],
  };

  const [initialValues, setInitialValues] = useState(() => {
    if (!updatedKnowledges?.length) {
      return [firstInitialValue];
    } else {
      return updatedKnowledges.map(({ key, value, bindItem }) => {
        return {
          key: getKey(key),
          value: getValue(value),
          bindItem,
        };
      });
    }
  });

  const [activeKey, setActiveKey] = useState('1');

  const [updateKey, setUpdateKey] = useState(0);

  const { visible, show, hide, meta } = useModal<number>();

  const ref = useRef<Record<number, HTMLElement | null>>({});

  return (
    <Formik enableReinitialize validateOnMount initialValues={initialValues} onSubmit={onSubmit}>
      {(formProps) => {
        if (!items) {
          return children(null, formProps);
        }
        const { values, setValues, errors, setFieldTouched, validateForm, touched, setTouched } =
          formProps;
        const drawer = meta !== undefined && (
          <ItemSelectV5Drawer
            key={meta}
            visible={visible}
            onClose={() => hide()}
            showSameSellerSkuTag={false}
            bindItem={meta === 0 ? bindItem : values[0].bindItem}
            renderFooter={(items) => {
              return (
                <div className="flex items-center justify-end text-[#515359]">
                  <Button
                    className="ml-[8px]"
                    type="primary"
                    onClick={() => {
                      hide();
                      const bindItem = items.map(item2bindItem);
                      setValues(
                        values.map((value, index) => {
                          if (index === meta) {
                            return {
                              ...value,
                              bindItem,
                            };
                          }
                          return {
                            ...value,
                            bindItem: value.bindItem
                              .map((value) => {
                                const target = bindItem.find(({ spuId }) => spuId === value.spuId);
                                if (!target) {
                                  return value;
                                }
                                const skus = value.skus.filter((_) =>
                                  target.skus.every(({ skuId }) => skuId !== _.skuId),
                                );
                                if (!skus.length && value.skus.length) {
                                  return undefined;
                                }
                                return {
                                  ...value,
                                  skus,
                                };
                              })
                              .filter((_) => _) as FV[0]['bindItem'],
                          };
                        }),
                      );
                    }}
                  >
                    {t('phrase.确定')}
                  </Button>
                </div>
              );
            }}
          />
        );
        const keyRender = (index: number) => (
          <FormField<FV[0]['key']>
            name={`${index}.key`}
            label={t('问题/属性')}
            validate={(value) =>
              value.trim() ? undefined : t('department.请输入x', { x: t('问题/属性') })
            }
          >
            {({ field }) => {
              return (
                <Input placeholder={t('department.请输入x', { x: t('问题/属性') })} {...field} />
              );
            }}
          </FormField>
        );
        const valueRender = (index: number) => (
          <FormField<FV[0]['value']>
            name={`${index}.value`}
            label={t('答案/值')}
            validate={(value) => {
              const fileValues = value.filter(({ msgType }) => msgType !== 'TEXT');
              const textValue = value.find(({ msgType }) => msgType === 'TEXT');
              if (!fileValues.length && !textValue?.message?.trim()) {
                return t('department.请输入x', { x: t('答案/值') });
              }
            }}
          >
            {({ field: { value, onChange, onBlur } }) => {
              return (
                <PureAnswerEditor
                  key={updateKey}
                  citeMode
                  region={region}
                  channel={channel}
                  storeId={storeId}
                  answers={value}
                  onAnswersChange={(answers) => {
                    onBlur();
                    onChange(answers);
                  }}
                />
              );
            }}
          </FormField>
        );
        const bindItemRender = (index: number, listOnly = false) => (
          <FormField<FV[0]['bindItem']>
            name={`${index}.bindItem`}
            noStyle
            validate={(bindItem) =>
              bindItem.length > 0 ? undefined : t('department.请选择x', { x: t('商品') })
            }
          >
            {({ field: { value, onBlur }, meta: { touched } }) => {
              return value.length ? (
                <FilterBarValueProvider>
                  <SpuSelectInner
                    listOnly={listOnly}
                    selectable={false}
                    spus={filterByBindItem(items, value)}
                    type={`${index}.bindItem`}
                  />
                </FilterBarValueProvider>
              ) : (
                <div className="relative">
                  <Button
                    className="!absolute top-[-66px] right-0"
                    type="link"
                    icon={<PlusOutlined />}
                    onClick={() => {
                      show(index);
                      onBlur();
                    }}
                  >
                    {t('aide.添加商品')}
                  </Button>
                  <Empty
                    className="!mt-8"
                    description={
                      touched ? (
                        <span className="ant-form-item-explain-error">
                          {t('department.请选择x', { x: t('商品') })}
                        </span>
                      ) : (
                        t('选择商品')
                      )
                    }
                  />
                </div>
              );
            }}
          </FormField>
        );
        const countRender = (bindItem: FV[0]['bindItem']) =>
          `${t('single_x_spu', { x: bindItem.length })} ${t('single_x_sku', {
            x: bindItem.reduce((res, { skus }) => res + skus.length, 0),
          })}`;
        const form = (
          <Container layout="vertical">
            <h3>
              {t('知识应用商品')}（{countRender(bindItem)}）
            </h3>
            <div>
              <Radio
                checked={initialValues.length === 1}
                onChange={({ target: { checked } }) =>
                  checked && setInitialValues([firstInitialValue])
                }
              >
                {t('修改并应用于全部商品')}
              </Radio>
              <Radio
                checked={initialValues.length > 1}
                onChange={({ target: { checked } }) => {
                  checked && setInitialValues([firstInitialValue, defaultInitialValue]);
                  setActiveKey('1');
                }}
              >
                {t('修改并拆分为多个知识')}
              </Radio>
            </div>
            <span />
            <section>
              <div ref={(dom) => (ref.current[0] = dom)}>
                <h4>{t('原知识详情')}</h4>
                {keyRender(0)}
                {valueRender(0)}
                {values.length > 1 && [
                  <h4>
                    {t('原知识应用商品')}（{countRender(values[0].bindItem)}）
                  </h4>,
                  <section>{bindItemRender(0, true)}</section>,
                ]}
              </div>
              {values.length > 1 ? (
                <Tabs
                  activeKey={activeKey}
                  onChange={setActiveKey}
                  size="small"
                  tabBarExtraContent={
                    <Button
                      size="small"
                      type="link"
                      icon={<PlusOutlined />}
                      onClick={() => {
                        validateForm().then((errors) => {
                          values.forEach((_, index) => {
                            setFieldTouched(`${index}.bindItem`, true, true);
                          });
                          const errorKeys = Object.keys(errors).filter(
                            (key) => errors[Number(key)],
                          );
                          if (!errorKeys.length) {
                            setValues([...values, defaultInitialValue]);
                            setActiveKey(`${values.length}`);
                          } else {
                            if (errors[0] && ref.current[0]) {
                              const error = ref.current[0].querySelector(
                                '.ant-form-item-explain-error',
                              );
                              if (error && error.innerHTML) {
                                error.scrollIntoView({ behavior: 'smooth', block: 'center' });
                              }
                            }
                            const errorTabs = errorKeys.filter((key) => Number(key) !== 0);
                            if (errorTabs.length) {
                              const minTab = Math.min(...errorTabs.map(Number));
                              setActiveKey(`${minTab}`);
                              setTimeout(() => {
                                const error = ref.current[minTab]?.querySelector(
                                  '.ant-form-item-explain-error',
                                );
                                if (error && error.innerHTML) {
                                  error.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                }
                              });
                            }
                          }
                        });
                      }}
                    >
                      {t('拆分更多知识')}
                    </Button>
                  }
                >
                  {values.slice(1).map(({ bindItem }, i) => {
                    const index = i + 1;
                    return (
                      <Tabs.TabPane key={`${index}`} tab={`${t('新增知识')} ${index}`}>
                        <section ref={(dom) => (ref.current[index] = dom)}>
                          <h4>{t('应用新的知识')}</h4>
                          {keyRender(index)}
                          {valueRender(index)}
                          <section>
                            <h4>
                              {t('已选择商品')}（{countRender(bindItem)}）
                            </h4>
                            <div>{bindItemRender(index)}</div>
                          </section>
                        </section>
                        <div>
                          <Button
                            disabled={index === 1 && (!!errors[1] || !values[1].bindItem.length)}
                            type="text"
                            icon={<DeleteFilled />}
                            onClick={() => {
                              Modal.confirm({
                                title: t('确定要删除该条【新增知识x】吗？', { x: index }),
                                onOk: () => {
                                  setActiveKey(values.length === 3 ? '1' : `${index - 1 || 1}`);
                                  if (values.length === 2) {
                                    setValues([
                                      {
                                        ...values[0],
                                        bindItem: firstInitialValue.bindItem,
                                      },
                                      {
                                        key: '',
                                        value: [],
                                        bindItem: [],
                                      },
                                    ]);
                                    setTouched([touched?.[0]]);
                                    setUpdateKey(updateKey + 1);
                                  } else {
                                    setValues([
                                      {
                                        ...values[0],
                                        bindItem: bindItem.reduce((res, cur) => {
                                          const targetIndex = res.findIndex(
                                            (_) => _.spuId === cur.spuId,
                                          );
                                          if (targetIndex < 0) {
                                            return [...res, cur];
                                          }
                                          return [
                                            ...res.slice(0, index),
                                            {
                                              ...res[targetIndex],
                                              skus: uniqBy(
                                                [...res[targetIndex].skus, ...cur.skus],
                                                'skuId',
                                              ),
                                            },
                                            ...res.slice(index + 1),
                                          ];
                                        }, values[0].bindItem),
                                      },
                                      ...values.slice(1, index),
                                      ...values.slice(index + 1),
                                    ]);
                                  }
                                },
                              });
                            }}
                          >
                            {t('phrase.删除')}
                          </Button>
                        </div>
                      </Tabs.TabPane>
                    );
                  })}
                </Tabs>
              ) : (
                bindItemRender(0)
              )}
              {drawer}
            </section>
          </Container>
        );
        return children(form, formProps);
      }}
    </Formik>
  );
}

const Tips = styled.div`
  display: flex;
  align-items: center;
  height: 68px;
  padding: 0 12px;
  margin-bottom: 12px;
  border-radius: 8px;
  background: linear-gradient(90deg, #c2d4ff -8.77%, #f6f9ff 106.46%);
  > img {
    width: 52px;
    margin-right: 24px;
    margin-top: -12px;
  }
  > div {
    font-size: 16px;
  }
`;

const Container = styled(Form)`
  display: flex;
  flex-direction: column;
  height: calc(100% - 80px);
  > h3 {
    position: relative;
    text-indent: 12px;
    &::before {
      content: '';
      display: inline-block;
      position: absolute;
      top: 4px;
      left: 0;
      width: 4px;
      height: 18px;
      background: var(--ant-primary-color);
    }
  }
  > span {
    height: 8px;
    margin: 16px -24px;
    background: #f7f9fa;
  }
  > section {
    flex: 1 0 0;
    display: flex;
    border: solid 1px #d3d7e0;
    overflow: hidden;
    h4 {
      position: relative;
      text-indent: 12px;
      &::before {
        content: '';
        display: inline-block;
        position: absolute;
        top: 2px;
        left: 0;
        width: 4px;
        height: 18px;
        background: var(--ant-primary-color);
      }
    }
    .ant-form-item-has-error .ant-form-item-control-input-content > div {
      border-color: var(--ant-error-color);
    }
    > div:first-of-type {
      flex: 0 0 auto;
      display: flex;
      flex-direction: column;
      width: 465px;
      padding: 16px;
      border-right: solid 1px #d3d7e0;
      overflow: auto;
      > section {
        flex: 1 0 0;
        min-height: 400px;
        margin: 0 -16px;
      }
    }
    > div + div {
      flex: 1 0 0;
    }
    > .ant-tabs {
      .ant-tabs-nav-wrap {
        padding: 0 16px;
      }
      .ant-tabs-content {
        height: 100%;
      }
      .ant-tabs-tabpane {
        display: flex;
        flex-direction: column;
        > section {
          flex: 1 0 0;
          display: flex;
          flex-direction: column;
          padding: 0 16px;
          overflow: auto;
          > section {
            flex: 1 0 0;
            display: flex;
            flex-direction: column;
            margin: 0 -16px;
            padding: 16px;
            background: #f2f5f7;
            > div {
              flex: 1 0 0;
              min-height: 400px;
              margin: 0 -16px;
            }
          }
        }
        > div {
          flex: 0 0 auto;
          display: flex;
          justify-content: flex-end;
          align-items: center;
          height: 56px;
          padding: 0 24px;
          > button {
            padding-right: 0;
            color: #000;
            opacity: 0.6;
            &.ant-btn[disabled] {
              opacity: 0.25;
            }
          }
        }
      }
    }
  }
`;

export default PageKnowledgeDrawer;
